import type { HeroClass } from '@somnhold/content';
import { perSecond, type BattleState, type CampaignState, type DeedBest, type DeedMode, type DeedRow, type FightDeeds, type HeroWorks } from '@somnhold/sim';
import { CLASS_ICON } from '../game/party-frames.ts';
import { getLang, t } from '../i18n.ts';
import { h } from '../ui/dom.ts';
import { fmtDuration } from '../ui/format.ts';
import { ICONS } from '../ui/icons.ts';

/**
 * The deeds: who dealt the most damage, healed the most, took the most blows and felled the most, as bars. One
 * fight's at its end (and on each wave of the history), and the whole campaign's in the hall of deeds.
 */

/** One hero's line on the board. `ticks`: the fighting time its per-second figures are over. */
export interface DeedLine {
  pid: string;
  name: string;
  cls?: HeroClass | undefined;
  ghost?: boolean;
  me?: boolean;
  row: DeedRow;
  ticks: number;
  fights?: number;
}

type Stat = 'dmg' | 'heal' | 'taken' | 'kills';
const STATS: Stat[] = ['dmg', 'heal', 'taken', 'kills'];
const COL: Record<Stat, 0 | 1 | 2 | 3> = { dmg: 0, heal: 1, taken: 2, kills: 3 };
const ICON: Record<Stat, string> = { dmg: ICONS.sword, heal: ICONS.heart, taken: ICONS.shield, kills: ICONS.skull };
const MODES: DeedMode[] = ['wave', 'incursion', 'crypt', 'expedition', 'maze', 'saga'];

let numFmt: Intl.NumberFormat | null = null;
let numLang = '';
/** 12.480 / 12,480: the reader's own way of grouping thousands. */
export const fmtNum = (n: number): string => {
  if (!numFmt || numLang !== getLang()) {
    numLang = getLang();
    numFmt = new Intl.NumberFormat(numLang === 'es' ? 'es-AR' : 'en-US');
  }
  return numFmt.format(n);
};

/** The tab last looked at, kept while the page lives (the next fight opens on it too). */
let lastStat: Stat = 'dmg';

/**
 * A hero's figure for one tab. Healing counts what was given to others: a barbarian drinking back its own blows
 * heals, but it isn't anyone's healer (the first records, before `aided`, only have the whole).
 */
const valOf = (l: DeedLine, st: Stat): number => (st === 'heal' ? (l.row[5] ?? l.row[1]) : l.row[COL[st]]);

/** The board: four tabs (damage, healing, taken, kills), each a ranking with bars; `titles` crowns one fight's best. */
export const deedsBoard = (lines: DeedLine[], opts: { titles?: boolean; test?: string } = {}): HTMLElement => {
  const has = (st: Stat): boolean => lines.some((l) => valOf(l, st) > 0);
  let stat: Stat = has(lastStat) ? lastStat : 'dmg';
  const list = h('ol.deeds-list', { 'aria-live': 'polite' });
  const tabs = h('div.deeds-tabs', { role: 'tablist' });
  const tabEls = new Map<Stat, HTMLElement>();
  for (const st of STATS) {
    const b = h(`button.deeds-tab.st-${st}`, { role: 'tab', type: 'button', 'data-stat': st, onclick: () => pick(st) }, h('span', { html: ICON[st] }), h('span.lbl', {}, t(`deeds.${st}`)));
    tabEls.set(st, b);
    tabs.append(b);
  }
  const box = h(`div.deeds.${opts.titles ? 'fight' : 'tally'}`, { 'data-test': opts.test ?? 'deeds' }, tabs, list);
  const render = (grow: boolean): void => {
    box.dataset.stat = stat;
    for (const [st, b] of tabEls) {
      b.classList.toggle('on', st === stat);
      b.setAttribute('aria-selected', String(st === stat));
    }
    const sorted = lines.slice().sort((a, b) => valOf(b, stat) - valOf(a, stat) || a.name.localeCompare(b.name));
    const top = Math.max(1, sorted[0] ? valOf(sorted[0], stat) : 0);
    const total = sorted.reduce((n, l) => n + valOf(l, stat), 0);
    const items: HTMLElement[] = [];
    sorted.forEach((l, i) => {
      const n = valOf(l, stat);
      const share = n / top;
      const self = stat === 'heal' ? l.row[1] - n : 0;
      // Damage and healing say how fast (healing, also what the hero gave itself); blows taken, what share of
      // the group's; kills, how often the hero fell.
      const sub =
        stat === 'dmg' || stat === 'heal'
          ? t('deeds.perSec', { n: fmtNum(perSecond(n, l.ticks)) }) + (self > 0 ? ` · ${t('deeds.selfHeal', { n: fmtNum(self) })}` : '')
          : stat === 'taken'
            ? total > 0 ? `${Math.round((100 * n) / total)}%` : ''
            : l.row[4] > 0 ? t(l.row[4] === 1 ? 'deeds.downsOne' : 'deeds.downs', { n: l.row[4] }) : t('deeds.noDowns');
      const fill = h('i.deeds-fill');
      fill.style.transform = `scaleX(${grow ? 0 : share})`;
      if (grow) requestAnimationFrame(() => requestAnimationFrame(() => (fill.style.transform = `scaleX(${share})`)));
      items.push(
        h(
          `li.deeds-row${l.me ? '.me' : ''}${n === 0 ? '.zero' : ''}`,
          { 'data-pid': l.pid },
          h('span.deeds-rank', {}, String(i + 1)),
          h('span.deeds-cls', { html: l.cls ? CLASS_ICON[l.cls] : ICONS.hero }),
          h(
            'span.deeds-bar',
            {},
            fill,
            h('span.deeds-name', {}, l.ghost ? h('span.deeds-ghost', { html: ICONS.ghost, title: t('deeds.ghost') }) : null, l.name, l.fights !== undefined ? h('small', {}, t(l.fights === 1 ? 'deeds.fightOne' : 'deeds.fights', { n: l.fights })) : null),
            h('span.deeds-val', {}, h('b.num', {}, fmtNum(n)), sub ? h('small', {}, sub) : null),
          ),
        ),
      );
    });
    list.replaceChildren(...items);
  };
  const pick = (st: Stat): void => {
    if (st === stat) return;
    stat = st;
    lastStat = st;
    render(true);
  };
  render(true);
  if (opts.titles) {
    const crowns = titlesOf(lines);
    if (crowns.childElementCount) box.append(crowns);
  }
  return box;
};

/**
 * One fight's best: a title for the top of each figure, only when someone actually did it, gathered per hero
 * (one who takes them all gets one line, not four). Same figures as the bars, so the crown matches the top one.
 */
const titlesOf = (lines: DeedLine[]): HTMLElement => {
  const box = h('div.deeds-titles');
  if (lines.length < 2) return box;
  const won = new Map<DeedLine, Stat[]>();
  for (const st of STATS) {
    const of = (l: DeedLine): number => valOf(l, st);
    // Ties go as the bars sort them: by name.
    const best = lines.reduce<DeedLine | null>((a, l) => (of(l) > 0 && (!a || of(l) > of(a) || (of(l) === of(a) && l.name.localeCompare(a.name) < 0)) ? l : a), null);
    if (best) won.set(best, [...(won.get(best) ?? []), st]);
  }
  for (const [l, sts] of won) {
    box.append(
      h(
        `div.deeds-honor${l.me ? '.me' : ''}`,
        {},
        h('span.deeds-honor-name', {}, l.name),
        h('span.deeds-honor-titles', {}, ...sts.map((st) => h(`span.deeds-crown.st-${st}`, { title: t(`deeds.title.${st}Why`) }, h('span', { html: ICON[st] }), t(`deeds.title.${st}`)))),
      ),
    );
  }
  return box;
};

/** One finished fight's lines: everyone in it but mercenaries, ghosts marked. */
export const fightLines = (b: BattleState, me: string, names?: Record<string, string>): DeedLine[] =>
  b.heroes
    .filter((x) => x.control !== 'merc')
    .map((x) => {
      const p = b.stats.players[x.pid];
      return {
        pid: x.pid,
        name: names?.[x.pid] || x.name || '—',
        cls: x.cls,
        ghost: x.control === 'ghost',
        me: x.pid === me,
        row: [p?.damage ?? 0, p?.healed ?? 0, p?.taken ?? 0, p?.kills ?? 0, p?.downs ?? 0, p?.aided ?? 0] as DeedRow,
        ticks: b.tick,
      };
    });

/** A wave's lines from its record (who was there live, and who as a ghost). */
export const waveLines = (s: CampaignState, d: FightDeeds, live: string[] | undefined, me?: string): DeedLine[] =>
  Object.entries(d.by).map(([pid, row]) => {
    const p = s.players.find((x) => x.id === pid);
    return { pid, name: p?.displayName ?? '—', cls: p?.hero.heroClass, ghost: live ? !live.includes(pid) : false, me: pid === me, row, ticks: d.ticks };
  });

/** Whether a fight left anything worth a board (a fight nobody struck in, like a lobby that never started, doesn't). */
export const anyDeeds = (lines: DeedLine[]): boolean => lines.some((l) => l.row[0] + l.row[1] + l.row[2] + l.row[3] > 0);

// ---------- the hall of deeds ----------

/** The campaign's hall of deeds: a ranking per kind of fight (or all of them), and each figure's best single fight. */
export const deedsPanel = (s: CampaignState, me: string, now: number): HTMLElement => {
  const all = s.deeds ?? {};
  const modes = MODES.filter((m) => Object.values(all).some((d) => (d.by[m]?.fights ?? 0) > 0));
  const wrap = h('div.deeds-hall', { 'data-test': 'deeds-hall' });
  wrap.append(h('p.deeds-intro', {}, t('deeds.intro')));
  const works = worksSection(s, me);
  if (!modes.length) {
    wrap.append(h('div.deeds-empty', {}, h('span', { html: ICONS.sword }), h('p', {}, t('deeds.empty'))));
    if (works) wrap.append(works);
    return wrap;
  }
  let mode: DeedMode | 'all' = 'all';
  const boardBox = h('div.deeds-board-box');
  const filters = h('div.deeds-modes', { role: 'tablist', 'aria-label': t('deeds.modes') });
  const chips = new Map<DeedMode | 'all', HTMLElement>();
  for (const m of ['all', ...modes] as (DeedMode | 'all')[]) {
    const c = h('button.deeds-mode', { type: 'button', role: 'tab', 'data-mode': m, onclick: () => { mode = m; draw(); } }, t(`deeds.mode.${m}`));
    chips.set(m, c);
    filters.append(c);
  }
  const draw = (): void => {
    for (const [m, c] of chips) {
      c.classList.toggle('on', m === mode);
      c.setAttribute('aria-selected', String(m === mode));
    }
    const lines: DeedLine[] = [];
    for (const p of s.players) {
      const d = all[p.id];
      if (!d) continue;
      const row: DeedRow = [0, 0, 0, 0, 0, 0];
      let ticks = 0;
      let fights = 0;
      for (const m of mode === 'all' ? modes : [mode]) {
        const tl = d.by[m];
        if (!tl) continue;
        for (let i = 0; i < 6; i++) row[i as 0] += tl.row[i as 0] ?? 0;
        ticks += tl.ticks;
        fights += tl.fights;
      }
      if (fights > 0) lines.push({ pid: p.id, name: p.displayName, cls: p.hero.heroClass, me: p.id === me, row, ticks, fights });
    }
    boardBox.replaceChildren(deedsBoard(lines, { test: 'deeds-board' }));
  };
  draw();
  wrap.append(filters, boardBox, recordsOf(s, now));
  if (works) wrap.append(works);
  return wrap;
};

/** Each figure's best single fight in the campaign, and whose it was. */
const recordsOf = (s: CampaignState, now: number): HTMLElement => {
  const box = h('div.deeds-records');
  const kinds: { key: 'dps' | 'hps' | 'taken' | 'kills'; stat: Stat }[] = [
    { key: 'dps', stat: 'dmg' },
    { key: 'hps', stat: 'heal' },
    { key: 'taken', stat: 'taken' },
    { key: 'kills', stat: 'kills' },
  ];
  for (const k of kinds) {
    let best: { b: DeedBest; name: string } | null = null;
    for (const p of s.players) {
      const b = s.deeds?.[p.id]?.best?.[k.key];
      if (b && (!best || b.n > best.b.n)) best = { b, name: p.displayName };
    }
    if (!best) continue;
    const val = k.key === 'dps' || k.key === 'hps' ? t('deeds.perSec', { n: fmtNum(best.b.n) }) : fmtNum(best.b.n);
    box.append(
      h(
        `div.deeds-record.st-${k.stat}`,
        {},
        h('span.deeds-record-ico', { html: ICON[k.stat] }),
        h('div', {}, h('small', {}, t(`deeds.record.${k.key}`)), h('b', {}, best.name), h('span.deeds-record-where', {}, `${t(`deeds.mode.${best.b.mode}`)} · ${t('log.ago', { t: fmtDuration(Math.max(0, now - best.b.at)) })}`)),
        h('span.deeds-record-val.num', {}, val),
      ),
    );
  }
  if (!box.childElementCount) return h('div');
  return h('section.deeds-records-wrap', {}, h('h4', {}, t('deeds.records')), box);
};

// ---------- the bastion: gathering and building ----------

type Work = 'gathered' | 'given' | 'workMin' | 'raised';
const WORKS: Work[] = ['gathered', 'given', 'workMin', 'raised'];
const WORK_ICON: Record<Work, string> = { gathered: ICONS.cart, given: ICONS.chest, workMin: ICONS.hammer, raised: ICONS.keep };
/** Materials brought home (dust goes on the line below: it doesn't build). */
const workOf = (w: HeroWorks, k: Work): number => (k === 'gathered' ? w.gathered.wood + w.gathered.stone + w.gathered.crystal + w.gathered.iron : w[k]);

let lastWork: Work = 'gathered';

/** The bastion's half of the hall: who brought the most home, gave the most, worked the most and raised the most. */
const worksSection = (s: CampaignState, me: string): HTMLElement | null => {
  const lines = s.players.flatMap((p) => {
    const w = s.deeds?.[p.id]?.works;
    return w ? [{ pid: p.id, name: p.displayName, cls: p.hero.heroClass, me: p.id === me, w }] : [];
  });
  if (!lines.some((l) => WORKS.some((k) => workOf(l.w, k) > 0) || l.w.gathered.dust > 0)) return null;
  let work: Work = lines.some((l) => workOf(l.w, lastWork) > 0) ? lastWork : (WORKS.find((k) => lines.some((l) => workOf(l.w, k) > 0)) ?? 'gathered');
  const list = h('ol.deeds-list', { 'aria-live': 'polite' });
  const tabs = h('div.deeds-tabs', { role: 'tablist' });
  const tabEls = new Map<Work, HTMLElement>();
  for (const k of WORKS) {
    const b = h(`button.deeds-tab.st-${k}`, { role: 'tab', type: 'button', 'data-stat': k, onclick: () => pick(k) }, h('span', { html: WORK_ICON[k] }), h('span.lbl', {}, t(`works.${k}`)));
    tabEls.set(k, b);
    tabs.append(b);
  }
  // What the picked tab counts, in one line: "Given" and "Raised" don't explain themselves.
  const hint = h('p.deeds-works-hint', { 'data-test': 'works-hint' });
  const box = h('div.deeds.tally', { 'data-test': 'works-board' }, tabs, hint, list);
  const render = (grow: boolean): void => {
    box.dataset.stat = work;
    hint.textContent = t(`works.${work}.desc`);
    for (const [k, b] of tabEls) {
      b.classList.toggle('on', k === work);
      b.setAttribute('aria-selected', String(k === work));
    }
    const sorted = lines.slice().sort((a, b) => workOf(b.w, work) - workOf(a.w, work) || a.name.localeCompare(b.name));
    const top = Math.max(1, sorted[0] ? workOf(sorted[0].w, work) : 0);
    const total = sorted.reduce((n, l) => n + workOf(l.w, work), 0);
    list.replaceChildren(
      ...sorted.map((l, i) => {
        const n = workOf(l.w, work);
        const share = n / top;
        // Gathering says the dust that came with it; giving and working, what share of the group's.
        const sub =
          work === 'gathered'
            ? l.w.gathered.dust > 0 ? t('works.dust', { n: fmtNum(l.w.gathered.dust) }) : ''
            : work === 'raised' || total === 0 ? '' : `${Math.round((100 * n) / total)}%`;
        const fill = h('i.deeds-fill');
        fill.style.transform = `scaleX(${grow ? 0 : share})`;
        if (grow) requestAnimationFrame(() => requestAnimationFrame(() => (fill.style.transform = `scaleX(${share})`)));
        return h(
          `li.deeds-row${l.me ? '.me' : ''}${n === 0 ? '.zero' : ''}`,
          { 'data-pid': l.pid },
          h('span.deeds-rank', {}, String(i + 1)),
          h('span.deeds-cls', { html: l.cls ? CLASS_ICON[l.cls] : ICONS.hero }),
          h(
            'span.deeds-bar',
            {},
            fill,
            h('span.deeds-name', {}, l.name),
            h('span.deeds-val', {}, h('b.num', {}, work === 'workMin' ? t('works.min', { n: fmtNum(n) }) : fmtNum(n)), sub ? h('small', {}, sub) : null),
          ),
        );
      }),
    );
  };
  const pick = (k: Work): void => {
    if (k === work) return;
    work = k;
    lastWork = k;
    render(true);
  };
  render(true);
  return h('section.deeds-works-wrap', {}, h('h4', {}, t('works.title')), h('p.deeds-works-intro', {}, t('works.intro')), box);
};
